import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Constants } from "@/static/Constants";
import { PrivacyFormService } from "@/service/PrivacyFormService";
import SubmitSuccess from "@/pages/SubmitSuccess";
import FormHeader from "@/components/FormHeader";
import FormSection from "@/components/FormSection";
import FormField from "@/components/FormField";
import FormConfirmation from "@/components/FormConfirmation";
import PrivacyInfoBox from "@/components/PrivacyInfoBox";
import "./PrivacyForm.css";

const INITIAL_FORM = {
    requestType: null,
    firstName: "",
    lastName: "",
    email: "",
};

export default function PrivacyForm() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const validate = () => {
        const newErrors = {};
        if (!form.requestType) newErrors.requestType = "Please select a request type.";
        if (!form.firstName.trim()) newErrors.firstName = "First name is required.";
        if (!form.lastName.trim()) newErrors.lastName = "Last name is required.";
        if (!form.email.trim()) {
            newErrors.email = "Email address is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = "Please enter a valid email address.";
        }
        return newErrors;
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setSubmitting(true);
        setSubmitError(false);
        try {
            await PrivacyFormService.submitRequest(form);
            setSubmitted(true);
        } catch {
            setSubmitError(true);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return <SubmitSuccess />;
    }

    return (
        <div className="pof-wrapper">
            <div className="pof-card">
                <FormHeader
                    title="Privacy & Data Opt-Out"
                    subtitle="Submit a request to access, correct, or delete your personal data, or to opt-out of data processing."
                />

                <PrivacyInfoBox
                    policyUrl={Constants.PRIVACY_POLICY_URL}
                    contactEmail={Constants.CONTACT_EMAIL}
                />

                <FormSection label="Request Type">
                    <FormField label="What would you like to do?" required error={errors.requestType}>
                        <Dropdown
                            value={form.requestType}
                            options={Constants.REQUEST_TYPES}
                            onChange={(e) => handleChange("requestType", e.value)}
                            placeholder="Select a request type"
                            className={`pof-dropdown${errors.requestType ? " p-invalid" : ""}`}
                        />
                    </FormField>
                </FormSection>

                <FormSection label="Your Information">
                    <div className="pof-row">
                        <FormField label="First Name" required half error={errors.firstName}>
                            <InputText
                                value={form.firstName}
                                onChange={(e) => handleChange("firstName", e.target.value)}
                                placeholder="Type your first name"
                                className={errors.firstName ? "p-invalid" : ""}
                            />
                        </FormField>
                        <FormField label="Last Name" required half error={errors.lastName}>
                            <InputText
                                value={form.lastName}
                                onChange={(e) => handleChange("lastName", e.target.value)}
                                placeholder="Type your last name"
                                className={errors.lastName ? "p-invalid" : ""}
                            />
                        </FormField>
                    </div>

                    <FormField label="Email Address" required error={errors.email}>
                        <InputText
                            value={form.email}
                            onChange={(e) => handleChange("email", e.target.value)}
                            placeholder="you@example.com"
                            type="email"
                            className={errors.email ? "p-invalid" : ""}
                        />
                    </FormField>
                </FormSection>

                {submitError && (
                    <Message
                        severity="error"
                        text="Something went wrong submitting your request. Please try again."
                        className="pof-submit-error"
                    />
                )}

                <FormConfirmation text="By submitting this form I confirm that the information I have provided is accurate." />

                <Button
                    label="Submit Request"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className="pof-submit-btn"
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={submitting}
                />
            </div>
        </div>
    );
}
